// Account Setup validation. Clients come from many countries: formats are
// deliberately tolerant (any alphabet, international phones, tax IDs and zip
// codes) and only reject what cannot be real data.
// Keep in sync with the validators in backend/server.py.

const FORBIDDEN_TEXT = /[<>\p{Cc}]/u;
const HAS_ALNUM = /[\p{L}\p{N}]/u;
const PHONE = /^[+＋]?[\p{Nd}\s().\-/（）－]+(?:\s*(?:ext\.?|x|int\.?|#|分机|转)\s*\p{Nd}{1,6})?$/iu;
const TAX_ID = /^[\p{L}\p{N}](?:[\p{L}\p{N}]|[ .\-/:#()－])*$/u;
const ZIP_OR_CODE = /^[\p{L}\p{N}](?:[\p{L}\p{N}]|[ .\-/])*$/u;
const EMAIL = /^[^\s@<>]+@[^\s@<>.]+(?:\.[^\s@<>.]+)*\.[^\s@<>.]{2,}$/u;
const URL_PATTERN = /^(?:https?:\/\/)?(?:[\p{L}\p{N}](?:[\p{L}\p{N}_-]*[\p{L}\p{N}])?\.)+\p{L}[\p{L}\p{N}_-]*[\p{L}\p{N}](?::\p{Nd}{1,5})?(?:[/?#][^\s<>]*)?$/iu;

const countDigits = (value) => (value.match(/\p{Nd}/gu) || []).length;

const CHECKS = {
  text: (v) => (FORBIDDEN_TEXT.test(v) || !HAS_ALNUM.test(v) ? 'text' : null),
  phone: (v) => (PHONE.test(v) && countDigits(v) >= 5 && countDigits(v) <= 20 ? null : 'phone'),
  taxId: (v) => (TAX_ID.test(v) && countDigits(v) > 0 ? null : 'taxId'),
  zip: (v) => (ZIP_OR_CODE.test(v) ? null : 'zip'),
  code: (v) => (ZIP_OR_CODE.test(v) ? null : 'code'),
  email: (v) => (EMAIL.test(v) ? null : 'email'),
  url: (v) => (URL_PATTERN.test(v) ? null : 'url'),
};

const rule = (check, max, required = false) => ({ check, max, required });
const NAME = rule('text', 150);
const SHORT = rule('text', 100);
const LONG = rule('text', 255);
const PHONE_RULE = rule('phone', 30);
const EMAIL_RULE = rule('email', 254);
const ZIP = rule('zip', 16);
const CODE = rule('code', 64);
const CURRENCY = rule('text', 40);
const required = (base) => ({ ...base, required: true });

const CLIENT_RULES = {
  customerName: required(NAME),
  companyName: required(NAME),
  taxId: rule('taxId', 40, true),
  phoneNumber: PHONE_RULE,
  address: required(LONG),
  district: SHORT,
  city: required(SHORT),
  state: required(SHORT),
  zipCode: ZIP,
  webpage: rule('url', 255),
};

const BANK_RULES = {
  beneficiaryName: NAME,
  bankName: NAME,
  accountNumber: CODE,
  swiftCode: CODE,
  bankAddress: LONG,
  bankCountry: SHORT,
  currency: CURRENCY,
  abaCode: CODE,
  corrBankName: NAME,
  corrAccountNumber: CODE,
  corrSwiftCode: CODE,
  corrAbaCode: CODE,
  corrBankAddress: LONG,
  corrCountry: SHORT,
  corrCurrency: CURRENCY,
  payoneerAccount: EMAIL_RULE,
  payoneerCurrency: CURRENCY,
};

const CONTACT_RULES = {
  commercialName: required(NAME),
  commercialPosition: SHORT,
  commercialPhone: PHONE_RULE,
  commercialEmail: required(EMAIL_RULE),
  commercialWechat: SHORT,
  commercialSkype: SHORT,
  operationsName: NAME,
  operationsPosition: SHORT,
  operationsPhone: PHONE_RULE,
  operationsEmail: EMAIL_RULE,
  operationsWechat: SHORT,
  operationsSkype: SHORT,
  financialName: NAME,
  financialPosition: SHORT,
  financialPhone: PHONE_RULE,
  financialCellPhone: PHONE_RULE,
  financialEmail: EMAIL_RULE,
  financialWechat: SHORT,
  financialSkype: SHORT,
};

const SHIPPING_RULES = {
  warehouseLegalName: required(NAME),
  warehouseManager: NAME,
  warehousePhone: PHONE_RULE,
  shippingAddress: required(LONG),
  city: required(SHORT),
  zipCode: ZIP,
  state: SHORT,
  country: required(SHORT),
};

const SECTION_RULES = [CLIENT_RULES, BANK_RULES, CONTACT_RULES];
const SHIPPING_SECTION = SECTION_RULES.length;
const PAYONEER_FIELDS = ['payoneerAccount', 'payoneerCurrency'];

// Shipping fields are named "<field>-<index>", e.g. "city-0"
const parseShippingName = (name) => {
  const match = /^(\w+)-(\d+)$/.exec(name);
  return match ? { field: match[1], index: Number(match[2]) } : null;
};

const getRule = (name) => {
  const shipping = parseShippingName(name);
  return shipping ? SHIPPING_RULES[shipping.field] : SECTION_RULES.map((rules) => rules[name]).find(Boolean);
};

const getValue = (name, formData) => {
  const shipping = parseShippingName(name);
  const value = shipping ? formData.shippingAddresses[shipping.index]?.[shipping.field] : formData[name];
  return (value || '').trim();
};

// Address A is mandatory; B and C only once something has been typed in them
const shippingAddressInUse = (index, formData) =>
  index === 0 || Object.keys(SHIPPING_RULES).some((field) => (formData.shippingAddresses[index]?.[field] || '').trim());

export const getMaxLength = (name) => getRule(name)?.max;

export const isFieldRequired = (name, formData) => {
  const shipping = parseShippingName(name);
  if (shipping && !shippingAddressInUse(shipping.index, formData)) return false;
  return Boolean(getRule(name)?.required);
};

// Returns an error code (see accountSetup.form.errors) or null
export const validateField = (name, formData) => {
  const fieldRule = getRule(name);
  if (!fieldRule) return null;
  const value = getValue(name, formData);
  if (!value) return isFieldRequired(name, formData) ? 'required' : null;
  if (value.length > fieldRule.max) return 'tooLong';
  return CHECKS[fieldRule.check](value);
};

const sectionFieldNames = (section, formData) => {
  if (section === SHIPPING_SECTION) {
    return formData.shippingAddresses.flatMap((_, index) =>
      Object.keys(SHIPPING_RULES).map((field) => `${field}-${index}`)
    );
  }
  const names = Object.keys(SECTION_RULES[section]);
  return formData.usePayoneer ? names : names.filter((name) => !PAYONEER_FIELDS.includes(name));
};

// Returns { fieldName: errorCode } for one section of the form
export const validateSection = (section, formData) =>
  sectionFieldNames(section, formData).reduce((errors, name) => {
    const code = validateField(name, formData);
    return code ? { ...errors, [name]: code } : errors;
  }, {});

export const validateForm = (formData) =>
  [0, 1, 2, SHIPPING_SECTION].reduce((errors, section) => ({ ...errors, ...validateSection(section, formData) }), {});

export const getFieldSection = (name) =>
  parseShippingName(name) ? SHIPPING_SECTION : SECTION_RULES.findIndex((rules) => rules[name]);

// Translates the 422 detail of POST /api/customer-form into form fields and codes
const BACKEND_SHIPPING_FIELDS = {
  legalName: 'warehouseLegalName',
  manager: 'warehouseManager',
  phone: 'warehousePhone',
  address: 'shippingAddress',
  city: 'city',
  zipCode: 'zipCode',
  state: 'state',
  country: 'country',
};

const BACKEND_ERROR_CODES = {
  missing: 'required',
  required: 'required',
  string_too_short: 'required',
  string_too_long: 'tooLong',
  invalid_text: 'text',
  invalid_phone: 'phone',
  invalid_tax_id: 'taxId',
  invalid_zip: 'zip',
  invalid_code: 'code',
  invalid_email: 'email',
  invalid_url: 'url',
};

export const mapBackendErrors = (detail) => {
  const errors = {};
  (Array.isArray(detail) ? detail : []).forEach(({ field, type }) => {
    if (!field) return;
    const shipping = /^shippingAddress(\d)_(\w+)$/.exec(field);
    const name = shipping ? `${BACKEND_SHIPPING_FIELDS[shipping[2]]}-${Number(shipping[1]) - 1}` : field;
    if (getRule(name)) errors[name] = BACKEND_ERROR_CODES[type] || 'invalid';
  });
  return errors;
};
